// composables/useAgents.ts
import {createAgentsValidator, patchAgentsValidator, updateAgentsValidator} from '~/validators/agent'
import {Agent} from '~/models/Agent'
import {useApiMonOrga} from '~/composables/api/useApiMonOrga'

type RawTag = {
    tagLib: string
    tagLibLong?: string | null
    tagColorType?: string
    deletedAt?: string | null
}

type RawAgent = {
    nni: string
    telMob?: string | null
    telFix?: string | null
    ageDesc?: string | null
    fct?: string | null
    sitLib?: string | null
    fsdum?: string | null
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    avatarDisplayMode?: 'teams' | 'initials' | 'avatar' | null
    avatarConfig?: Record<string, unknown> | null
    tags?: RawTag[]
    equLib?: string[]
}

function mapToAgent(raw: RawAgent): Agent {
    const visibleTags = (raw.tags ?? []).filter((tag) => !tag.deletedAt)
    return new Agent({
        nni: raw.nni,
        telMob: raw.telMob ?? '',
        telFix: raw.telFix ?? '',
        ageDesc: raw.ageDesc ?? '',
        fct: raw.fct ?? null,
        sitLib: raw.sitLib ?? '',
        fsdum: raw.fsdum ?? '',
        tags: visibleTags.map(tag => ({
            tagLib: tag.tagLib,
            tagLibLong: tag.tagLibLong ?? null,
            tagColorType: tag.tagColorType,
        })),
        firstName: raw.firstName ?? null,
        lastName: raw.lastName ?? null,
        email: raw.email ?? null,
        avatarDisplayMode: raw.avatarDisplayMode ?? null,
        avatarConfig: raw.avatarConfig ?? null,
    })
}

export const useAgents = () => {
    async function storeAgent(agentData: Agent): Promise<Agent | null> {
        const payload = await createAgentsValidator.validate(agentData)
        const data = await useApiMonOrga<RawAgent>('POST', 'agents', undefined, payload)
        return data ? mapToAgent(data) : null
    }

    async function fetchAgentById(
        id: string,
    ): Promise<null | { agent: Agent; equLibs: string[] }> {
        try {
            const data = await useApiMonOrga<RawAgent>('GET', `agents/${id}`)

            if (!data) return null

            const agent = mapToAgent(data)

            return {
                agent,
                equLibs: data.equLib ?? [],
            }
        } catch (error: unknown) {
            // Agent pas encore en base (ajout depuis l’annuaire) : 404 attendu → null.
            const status =
                (error as { status?: number; statusCode?: number })?.status
                ?? (error as { statusCode?: number })?.statusCode
            if (status === 404) return null
            throw error
        }
    }

    async function updateAgent(nni: string, agentData: Agent): Promise<Agent | null> {
        const payload = await patchAgentsValidator.validate(agentData)
        const data = await useApiMonOrga<RawAgent>('PATCH', `agents/${nni}`, undefined, payload)
        return data ? mapToAgent(data) : null
    }

    async function patchAgent(
        nni: string,
        partialData: Partial<Agent>,
    ): Promise<Agent | null> {
        const payload = await updateAgentsValidator.validate(partialData)
        const data = await useApiMonOrga<RawAgent>('PATCH', `agents/${nni}`, undefined, payload)
        return data ? mapToAgent(data) : null
    }

    async function deleteAgent(nni: string): Promise<boolean> {
        await useApiMonOrga<unknown>('DELETE', `agents/${nni}`)
        return true
    }

    return {
        storeAgent,
        fetchAgentById,
        updateAgent,
        patchAgent,
        deleteAgent,
    }
}
